import * as THREE from 'three';
import { sceneMovementManager } from './scene-movement-manager';
import { requestSceneConfig } from './api-connector';
import { startLiveSync } from './live-sync';
import { RuntimeEngine } from '@spotar/runtime';
import { AnimationManager } from './animation-manager';
import { AudioManager } from './audio-manager';
import {
  initPlayAnimationImplementation,
  initAnimation,
} from './node-implementations/play-animation-implementation';
import { initPlayAudioImplementation } from './node-implementations/play-audio-implementation';
import { setAiRequestContext } from './cms-ai-api';
import { notifyViewerFailure } from './error-notifications';
import { initStopPlaybackImplementation } from './node-implementations/stop-playback-implementation';
import { createSceneHelper, printSceneHierarchy, sceneHelper } from './helper/scene-helper';
import {
  nodeTypeRequiresAnimation,
  nodeTypeRequiresAudio,
  nodeTypeRequiresAudioPlayer,
  nodeTypeRequiresTrayChoice,
  nodeTypeRequiresVideo,
} from './helper/node-requirements-helper';
import { createGLTFLoader } from './model-loaders';
import { resolveCmsDevProxyUrl } from './cms-dev-proxy-url';
import type {
  Group,
  SceneObject,
  SpotSceneResponse,
  SpotSceneResponseAsset,
} from '../types/spot-scene-response.gen';
import type { XrFramework } from './helper/device-helper';
import type { Camera, Object3D, Scene, WebGLRenderer } from 'three';
import type { GLTF, GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { debugLog } from './debug-log';
import { initAudioPlayerLoadTrackImplementation } from './node-implementations/audioplayer-load-track-implementation';
import { initAudioPlayerPlayImplementation } from './node-implementations/audioplayer-play-implementation';
import { initAudioPlayerPauseImplementation } from './node-implementations/audioplayer-pause-implementation';
import { initAudioPlayerShowUiImplementation } from './node-implementations/audioplayer-show-ui-implementation';
import { initTraySpawnChoiceImplementation } from './node-implementations/tray-spawn-choice-implementation';
import { initOnClickImplementation } from './node-implementations/on-click-implementation';
import { initOnPlayerEventsImplementation } from './node-implementations/on-player-events-implementation';
import { addContactShadowPlanes } from './contact-shadow';
import { collectNodeImplementations } from './node-implementations-collector';
import { SCENE_LOADED_EVENT, markSceneAsLoaded } from './scene-load-state';
import {
  applyHitboxRaycastPolicy,
  applyInvisibleHitboxes,
  attachSceneHitboxes,
} from './helper/hitbox-helper';
import { readObjectRefId } from './helper/object-ref';
import type { ClickTarget } from './click-manager';

export let AREngineForIOS: XrFramework = '8th-wall';

THREE.Cache.enabled = true;

export let showVRViewBeforeAR = true;

const urlParams = new URLSearchParams(window.location.search);
export const spotId = urlParams.get('spotId');

if (!spotId) {
  notifyViewerFailure({ title: 'Dieser Link ist unvollständig (spotId fehlt)' });
  throw new Error('Missing required query parameter: spotId');
}
setAiRequestContext({ spotId });
const { default: AudioPlayer } = await import('./audioplayer');
const { default: TrayChoice } = await import('./tray-choice');

let runtimeEngine: RuntimeEngine | null = null;
let animationManager: AnimationManager | null = null;
let audioManager: AudioManager | null = null;
let audioPlayer: AudioPlayer | null = null;
let trayChoice: InstanceType<typeof TrayChoice> | null = null;
let sceneRoot: Object3D | null = null;

const sceneConfig: SpotSceneResponse = await loadSceneConfig(spotId);
console.log('sceneConfig', sceneConfig);

/** Nothing works without the config, so a failed load must not stay in the console. */
async function loadSceneConfig(id: string): Promise<SpotSceneResponse> {
  try {
    return await requestSceneConfig(id);
  } catch (error) {
    notifyViewerFailure({ title: 'Die Szene konnte nicht geladen werden', error });
    throw error;
  }
}


startLiveSync(spotId);

const updateSceneCalled = new Event('updateSceneCalled');

const { ClickManager: ClickManagerClass } = await import('./click-manager');
const clickManager = new ClickManagerClass();

export type InitSceneOptions = {
  scene: Scene;
  modelRoot?: Object3D;
  defaultObjectPosition?: THREE.Vector3;
  renderer?: WebGLRenderer;
  camera?: Camera;
  clickReceiver?: HTMLElement;
  isVrOrbitView?: boolean;
  backgroundScene?: Scene;
};

export function updateSceneContent() {
  window.dispatchEvent(updateSceneCalled);
}

export function dispatchSceneLoadedEvent() {
  markSceneAsLoaded();
}

function calculateShadowAfterFirstAnimationFrame(
  scene: Scene,
  modelContentRoot: Object3D,
  renderer?: WebGLRenderer
) {
  const setup = () => {
    addContactShadowPlanes({
      scene,
      modelRoot: modelContentRoot,
      renderer,
      intensity: 0.5,
      softness: 2,
    });
  };

  window.addEventListener(
    SCENE_LOADED_EVENT,
    () => {
      if (animationManager) {
        window.addEventListener('animationsUpdated', setup, { once: true });
      } else {
        setup();
      }
    },
    { once: true }
  );
}

export async function initScene(
  options: InitSceneOptions = {} as InitSceneOptions,
  onGLBLoaded?: (modelRoot: Object3D) => void
) {
  const {
    scene,
    modelRoot,
    renderer: optionsRenderer,
    camera,
    clickReceiver,
    isVrOrbitView = false,
  } = options;

  createSceneLights(scene);
  createSceneHelper(scene);

  let modelContentRoot: Object3D;
  if (modelRoot) {
    modelContentRoot = modelRoot;
  } else {
    modelContentRoot = new THREE.Group();
    scene.add(modelContentRoot);
  }

  sceneRoot = modelContentRoot;

  initNodeImplementations();

  // Scene objects must exist before value nodes like getHitbox resolve (and before
  // click/hitbox events subscribe with a resolved target id).
  await loadSceneObjects(sceneConfig, optionsRenderer);
  sceneHelper!.rebuildSkinnedMeshes();
  calculateShadowAfterFirstAnimationFrame(scene, modelContentRoot, optionsRenderer);

  if (!runtimeEngine) {
    runtimeEngine = new RuntimeEngine();
    await initRuntimeEngine();
  }

  const clickTargets = collectClickTargetsFromRuntime();
  clickManager.initOnClickImplementation(clickTargets);
  applyHitboxRaycastPolicy(scene, new Set(clickTargets.map((target) => target.id)));

  if (sceneConfig.scene.skySphere?.enabled) {
    const skySphereTextureID = sceneConfig.scene.skySphere.background.assetId;
    const skySphereTextureURL = resolveCmsDevProxyUrl(
      sceneConfig.assets.find((asset) => asset.id === skySphereTextureID)?.url
    );
    await sceneHelper!.setSceneBackground(
      skySphereTextureURL!,
      THREE.EquirectangularReflectionMapping,
      THREE.SRGBColorSpace
    );
  }

  if (onGLBLoaded) onGLBLoaded(modelContentRoot);

  if (camera && clickReceiver) {
    clickManager.init({
      camera,
      renderer: optionsRenderer!,
      scene: modelContentRoot,
      newClickReceiver: clickReceiver,
      isVrOrbitView,
    });
    sceneMovementManager.init(modelContentRoot, camera, optionsRenderer ?? null);
  }

  //printSceneHierarchy(modelContentRoot);
}

/**
 * Custom-function bodies (`graphs.subGraphs`) can contain the full node spectrum,
 * including events. Capability scans must include them, otherwise nodes used only
 * inside a function never receive their runtime implementation and silently no-op.
 */
function getAllGraphNodes(): SpotSceneResponse['graphs']['main']['nodes'] {
  const subGraphNodes = Object.values(sceneConfig.graphs.subGraphs ?? {}).flatMap(
    (subGraph) => subGraph.nodes
  );
  return [...sceneConfig.graphs.main.nodes, ...subGraphNodes];
}

function isClickEventNode(nodeType: string): boolean {
  return nodeType === 'onClick' || nodeType === 'onHitboxClick';
}

function readsUseMeshFlag(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return !!(data as { useMesh?: unknown }).useMesh;
}

function collectClickTargets(
  nodes: SpotSceneResponse['graphs']['main']['nodes']
): ClickTarget[] {
  const targets: ClickTarget[] = [];

  for (const node of nodes) {
    if (!isClickEventNode(node.type)) continue;

    const targetId = node.data?.target?.id;
    if (typeof targetId !== 'string' || targetId.length === 0) continue;

    // onClick defaults to bounding-box hitbox; useMesh opts into precise mesh hits.
    // onHitboxClick always uses the hitbox mesh geometry.
    const useBoundingBox = node.type === 'onClick' && !readsUseMeshFlag(node.data);

    targets.push({
      id: targetId,
      useBoundingBox,
    });
  }

  return targets;
}

/**
 * Prefer runtime node data (includes value-resolved targets like getHitbox).
 * Falls back to static graph JSON when the engine is not ready.
 */
function collectClickTargetsFromRuntime(): ClickTarget[] {
  const runtimeNodes = runtimeEngine?.nodes;
  if (!runtimeNodes?.length) {
    return collectClickTargets(getAllGraphNodes());
  }

  const targets: ClickTarget[] = [];
  for (const node of runtimeNodes) {
    if (!isClickEventNode(node.type)) continue;

    const targetId = readObjectRefId(node.data.target);
    if (!targetId) continue;

    targets.push({
      id: targetId,
      useBoundingBox: node.type === 'onClick' && !readsUseMeshFlag(node.data),
    });
  }

  return targets;
}

function initNodeImplementations() {
  const allNodes = getAllGraphNodes();
  const animationsRequired : boolean =  allNodes.some((node) => nodeTypeRequiresAnimation(node.type));
  const audioRequired : boolean =  allNodes.some((node) => nodeTypeRequiresAudio(node.type));
  const videoRequired : boolean =  allNodes.some((node) => nodeTypeRequiresVideo(node.type));
  const audioPlayerRequired : boolean =  allNodes.some((node) => nodeTypeRequiresAudioPlayer(node.type));
  const trayChoiceRequired : boolean =  allNodes.some((node) => nodeTypeRequiresTrayChoice(node.type));
  const onClickRequired : boolean =  allNodes.some(
    (node) => node.type === 'onClick' || node.type === 'onHitboxClick'
  );

  if(animationsRequired || audioRequired || videoRequired || audioPlayerRequired) {
    audioManager = new AudioManager();
    initPlayAudioImplementation(audioManager);
    animationManager?.dispose();
    animationManager = new AnimationManager();
    initPlayAnimationImplementation(animationManager);
    initStopPlaybackImplementation(animationManager, audioManager);  
  }
  const primaryColor = '#da131c';
  document.documentElement.style.setProperty('--primary-color', primaryColor);

  if(audioPlayerRequired) {
    const audioPlayerExists = document.getElementById('untertitelWrapper');
    if (!audioPlayerExists) {
      audioPlayer = new AudioPlayer({
        primaryColor,
        bottomOffset: '10px',
      });
    }
    initAudioPlayerLoadTrackImplementation(audioPlayer!);
    initAudioPlayerPlayImplementation(audioPlayer!);
    initAudioPlayerPauseImplementation(audioPlayer!);
    initAudioPlayerShowUiImplementation(audioPlayer!);
    initOnPlayerEventsImplementation(audioPlayer!);
  }
  if(trayChoiceRequired) {
    const isTrayChoiceStale = !document.getElementById('tray-choice-root');
    if (!trayChoice || isTrayChoiceStale) {
      trayChoice = new TrayChoice({
        primaryColor,
      });
    }
    initTraySpawnChoiceImplementation(trayChoice);
  }
  if (onClickRequired) {
    initOnClickImplementation(clickManager);
  }

}

function orderedSceneAssets(items: SpotSceneResponseAsset[]) {
  if (!items?.length) return [];
  const list = items.slice();
  const hasPriority = list.some((o) => (o as SpotSceneResponseAsset & { priority?: number }).priority != null);
  if (!hasPriority) return list;
  return list.sort(
    (a, b) =>
      ((b as SpotSceneResponseAsset & { priority?: number }).priority ?? b.loadPriority ?? 0) -
      ((a as SpotSceneResponseAsset & { priority?: number }).priority ?? a.loadPriority ?? 0)
  );
}

async function addGLTF(
  loader: GLTFLoader,
  url: string,
  assetUrl: string,
  object: SceneObject
) {
  let gltf: GLTF;
  try {
    gltf = await loader.loadAsync(url);
  } catch (err) {
    console.error('Error loading scene glb:', assetUrl, err);
    notifyViewerFailure({
      title: 'Ein 3D-Modell konnte nicht geladen werden',
      error: err,
    });
    return;
  }
  if (animationManager && object.uuid) {
    initAnimation(object.uuid, gltf);
  }
  gltf.scene.uuid = object.uuid;
  gltf.scene.userData.isSceneObject = true;
  if(object.groupId == null)
    sceneRoot!.add(gltf.scene);
  else
    sceneHelper!.getObjectByUUID(object.groupId)!.add(gltf.scene);
  applyObjectTransform(
    gltf.scene,
    object.localPosition,
    object.localRotation,
    object.localScale
  );
  attachSceneHitboxes(gltf.scene, findHitboxesForHost(object.uuid));
  applyInvisibleHitboxes(gltf.scene);
}

function findHitboxesForHost(hostUuid: string) {
  const hitboxes = sceneConfig.scene.hitboxes ?? [];
  return hitboxes.filter((hitbox) => hitbox.hostUuid === hostUuid);
}

function applyObjectTransform(
  object: Object3D,
  localPosition: number[],
  localRotation: number[],
  localScale: number[]
) {
  object.position.set(localPosition[0], localPosition[1], localPosition[2]);
  object.rotation.set(localRotation[0], localRotation[1], localRotation[2]);
  object.scale.set(localScale[0], localScale[1], localScale[2]);
}

function addGroup(groupConfig: Group): THREE.Group {
  const group = new THREE.Group();
  group.userData.isSceneGroup = true;
  group.name = groupConfig.name;
  if (groupConfig.uuid) group.uuid = groupConfig.uuid;
  applyObjectTransform(
    group,
    groupConfig.position,
    groupConfig.rotation,
    groupConfig.scale
  );
  if(groupConfig.parentGroupId == null)
  {
    sceneRoot!.add(group);
  }
  else
  {
    sceneHelper!.getObjectByUUID(groupConfig.parentGroupId)!.add(group);
  }
  return group;
}

async function loadSceneObjects(
  config: SpotSceneResponse,
  renderer?: WebGLRenderer
) {
  const loader = createGLTFLoader(renderer);
  const gltfAssets = orderedSceneAssets(
    config.assets.filter((asset) => asset.kind === 'GLTF')
  );
  for (const groupConfig of config.scene.groups) {
    addGroup(groupConfig);
  }
  for (const gltfAsset of gltfAssets) {
    const url = resolveCmsDevProxyUrl(gltfAsset.url)!;
    for (const object of config.scene.objects) {
      if (object.assetId === gltfAsset.id) {
        await addGLTF(loader, url, gltfAsset.url, object);
      }
    }
  }
}

function createSceneLights(parent: Object3D) {
  const ambient = new THREE.AmbientLight(0xffffff, 1.6);
  parent.add(ambient);
  const dir = new THREE.DirectionalLight(0xffffff, 1.2);
  dir.position.set(0.5, 1, 0.25);
  parent.add(dir);
}

async function initRuntimeEngine() {
  const graph = sceneConfig.graphs.main;
  const subGraphs = (sceneConfig.graphs.subGraphs ?? {}) as Record<
    string,
    typeof graph
  >;
  logFunctionInventory(subGraphs);
  const variables = extractSceneVariables(sceneConfig);
  await runtimeEngine!.init(graph, collectNodeImplementations(), subGraphs, {
    variables,
  });
}

function extractSceneVariables(config: SpotSceneResponse): Array<{
  id: string;
  defaultValue?: unknown;
  isConst?: boolean;
  scope?: 'graph' | 'local' | 'project';
  functionId?: string;
}> {
  const fromResponse = (config as SpotSceneResponse & { variables?: unknown[] }).variables;
  if (Array.isArray(fromResponse) && fromResponse.length > 0) {
    return fromResponse.filter(
      (entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object'
    ).map((entry) => ({
      id: String(entry.id ?? ''),
      defaultValue: entry.defaultValue,
      isConst: entry.isConst === true,
      scope:
        entry.scope === 'local'
          ? 'local'
          : entry.scope === 'project'
            ? 'project'
            : 'graph',
      functionId: typeof entry.functionId === 'string' ? entry.functionId : undefined,
    })).filter((entry) => entry.id.length > 0);
  }

  const meta = (config.graphs.main as { meta?: Record<string, unknown> }).meta;
  const fromMeta = meta?.spotarVariables;
  if (!Array.isArray(fromMeta)) return [];
  return fromMeta.filter(
    (entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object'
  ).map((entry) => ({
    id: String(entry.id ?? ''),
    defaultValue: entry.defaultValue,
    isConst: entry.isConst === true,
    scope:
      entry.scope === 'local'
        ? 'local'
        : entry.scope === 'project'
          ? 'project'
          : 'graph',
    functionId: typeof entry.functionId === 'string' ? entry.functionId : undefined,
  })).filter((entry) => entry.id.length > 0);
}

/**
 * A callFunction node whose `functionId` has no body in `graphs.subGraphs` fails
 * silently from the user's perspective, so make the delivered inventory visible.
 */
function logFunctionInventory(subGraphs: Record<string, { nodes: unknown[]; connections: unknown[] }>) {
  const requestedFunctionIds = sceneConfig.graphs.main.nodes
    .filter((node) => node.type === 'callFunction')
    .map((node) => (node.data as { functionId?: string }).functionId);

  console.log('[graph] function bodies delivered by CMS', {
    functionIds: Object.keys(subGraphs),
    bodies: Object.fromEntries(
      Object.entries(subGraphs).map(([id, body]) => [
        id,
        { nodes: body.nodes?.length ?? 0, connections: body.connections?.length ?? 0 },
      ])
    ),
    calledFromMain: requestedFunctionIds,
    unresolved: requestedFunctionIds.filter((id) => !id || !subGraphs[id]),
  });
}
